/**
 * Iter XX.79 — Microsoft Clarity integration.
 *
 * Loaded once at the app root (see App.js). Provides:
 *   · init(projectId)               — async loader (safe to call once)
 *   · track(name, meta?)            — fire a custom event
 *   · set(key, value)               — custom tag on the current session
 *   · identify(userId, sessionId?)  — attach a user id to the session
 *   · stopRecording() / startRecording()
 *
 * Everything is a no-op if `REACT_APP_CLARITY_PROJECT_ID` is not set
 * OR if the caller's browser blocks the tag (ad-blockers eat ~15-20%
 * of Clarity requests — do not treat this analytics as ground truth).
 *
 * Also exposes CLARITY_MASK & CLARITY_UNMASK constants so components
 * can spread { ...CLARITY_MASK } onto a wrapping <div> without
 * remembering the exact `data-clarity-mask` attribute name.
 */

const _projectIdEnv = process.env.REACT_APP_CLARITY_PROJECT_ID || "";

// Ad-blockers cause init calls to fail silently. Track once so we
// don't spam retries.
let _initTried = false;

function _hasClarity() {
  return typeof window !== "undefined" && typeof window.clarity === "function";
}

export function init(projectId) {
  const id = (projectId || _projectIdEnv || "").trim();
  if (!id) return false;
  if (typeof window === "undefined") return false;
  if (_initTried || _hasClarity()) return true;
  _initTried = true;
  // Microsoft's official async loader snippet, converted from the
  // packed one-liner to something maintainable.
  try {
    // eslint-disable-next-line func-names
    (function (c, l, a, r, i) {
      c[a] = c[a] || function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
      const t = l.createElement(r);
      t.async = 1;
      t.src = "https://www.clarity.ms/tag/" + i;
      const y = l.getElementsByTagName(r)[0];
      y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", id);
    return true;
  } catch {
    return false;
  }
}

export function track(name, meta) {
  if (!name) return;
  try {
    if (_hasClarity()) {
      window.clarity("event", String(name));
      // Attach a lightweight breadcrumb tag as well so recordings can
      // be filtered even if the "event" replay filter is missed.
      if (meta && typeof meta === "object") {
        Object.entries(meta).forEach(([k, v]) => {
          if (v == null) return;
          try { window.clarity("set", `evt_${String(k)}`, String(v)); }
          catch { /* noop */ }
        });
      }
    }
  } catch { /* noop */ }
  // Iter XX.80 — Fan out to Meta Pixel with the SAME event name +
  // payload. Meta module short-circuits if the pixel isn't configured
  // (env var missing) so this is safe to call unconditionally.
  try {
    // eslint-disable-next-line global-require
    const meta_ = require("./metaPixel");
    if (meta_ && typeof meta_.track === "function") {
      meta_.track(name, meta);
    }
  } catch { /* noop */ }
}

export function set(key, value) {
  try {
    if (_hasClarity()) window.clarity("set", String(key), String(value));
  } catch { /* noop */ }
}

export function identify(userId, sessionId) {
  try {
    if (_hasClarity() && userId) {
      window.clarity("identify", String(userId),
                     sessionId ? String(sessionId) : undefined);
    }
  } catch { /* noop */ }
}

export function grantConsent() {
  try { if (_hasClarity()) window.clarity("consent"); }
  catch { /* noop */ }
}

export function stopRecording() {
  try { if (_hasClarity()) window.clarity("stop"); }
  catch { /* noop */ }
}

export function startRecording() {
  try { if (_hasClarity()) window.clarity("start"); }
  catch { /* noop */ }
}

/**
 * Iter XX.79 · Balanced masking (per founder decision).
 *
 * Anything PII-ish gets `data-clarity-mask="true"`. Ordinary cricket
 * chat text stays visible so the founder can see drop-off /
 * confusion in real recordings.
 *
 * Usage:
 *   <input {...CLARITY_MASK} type="email" ... />
 *   <div  {...CLARITY_MASK} >{playerEmail}</div>
 */
export const CLARITY_MASK = { "data-clarity-mask": "true" };
export const CLARITY_UNMASK = { "data-clarity-unmask": "true" };

// Route slugs where recordings must be stopped entirely (payments,
// billing, account management). Session recording resumes when the
// user leaves these routes.
export const SENSITIVE_ROUTES = [
  "/plus",             // upgrade / checkout kickoff
  "/plus/success",     // post-payment confirmation
  "/login",            // auth
  "/coach/signin",     // coach auth
  "/me",               // profile / account settings
  "/partner/edit",     // partner account
  "/partner/wallet",   // wallet / earnings
];

export function isSensitivePath(pathname) {
  if (!pathname) return false;
  const p = pathname.toLowerCase();
  return SENSITIVE_ROUTES.some(
    (r) => p === r || p.startsWith(r + "/"),
  );
}
